import { fetchSubscriptionStatusViaBff } from '~/utils/billing'
import { computed } from 'vue'
import { useNuxtApp, useRuntimeConfig, useState } from '#app'
import { resolveStripeEnabled } from '~/components/Utilities/billingFeatureFlag'
import { INACTIVE_BILLING_SUBSCRIPTION_RESPONSE } from '~~/shared/billing/contracts'
import type { BillingSubscriptionStatus } from '~~/shared/billing/subscription-status'

interface BillingStatusState {
  hasLoaded: boolean
  status: BillingSubscriptionStatus
  trialDaysRemaining: number
  userId: string | null
}

const BILLING_STATUS_STATE_KEY = 'billing-status'

function buildDefaultState(): BillingStatusState {
  return {
    hasLoaded: false,
    status: INACTIVE_BILLING_SUBSCRIPTION_RESPONSE.status,
    trialDaysRemaining:
      INACTIVE_BILLING_SUBSCRIPTION_RESPONSE.trial_days_remaining,
    userId: null,
  }
}

function resolveCurrentUserId(keycloak?: {
  tokenParsed?: { sub?: string }
}): string | null {
  const userId = keycloak?.tokenParsed?.sub

  return typeof userId === 'string' && userId.trim().length > 0
    ? userId.trim()
    : null
}

export function useBillingStatus() {
  const nuxtApp = useNuxtApp()
  const runtimeConfig = useRuntimeConfig()
  const state = useState<BillingStatusState>(BILLING_STATUS_STATE_KEY, () =>
    buildDefaultState()
  )

  const resetBillingStatus = () => {
    state.value = buildDefaultState()
  }

  const refreshBillingStatus = async (
    options: { forceRefresh?: boolean } = {}
  ) => {
    const { forceRefresh = false } = options
    const token = nuxtApp.$keycloak?.token
    const userId = resolveCurrentUserId(nuxtApp.$keycloak)

    if (!resolveStripeEnabled(runtimeConfig.public) || !token || !userId) {
      resetBillingStatus()
      return
    }

    if (state.value.userId && state.value.userId !== userId) {
      resetBillingStatus()
    }

    const response = await fetchSubscriptionStatusViaBff(token, {
      forceRefresh,
    })

    if (resolveCurrentUserId(nuxtApp.$keycloak) !== userId) {
      return
    }

    state.value = {
      hasLoaded: true,
      status: response?.status || INACTIVE_BILLING_SUBSCRIPTION_RESPONSE.status,
      trialDaysRemaining:
        response?.trial_days_remaining ??
        INACTIVE_BILLING_SUBSCRIPTION_RESPONSE.trial_days_remaining,
      userId,
    }
  }

  const ensureBillingStatusLoaded = async () => {
    const userId = resolveCurrentUserId(nuxtApp.$keycloak)

    if (state.value.hasLoaded && state.value.userId === userId && userId) {
      return
    }

    await refreshBillingStatus()
  }

  return {
    billingStatus: computed(() => state.value.status),
    trialDaysRemaining: computed(() => state.value.trialDaysRemaining),
    refreshBillingStatus,
    ensureBillingStatusLoaded,
  }
}
